/**
 * for more details on how this should work, please see:
 *      https://williamhill.jira.com/wiki/display/BORNS/Live+Score+Commentary
 *
 *
 * @TODO: Compare with EventViewAdapter's api.livescore, see if it can get merged as the difference seems to be in
 *     the target
 *
 * @module app/views/ScoreboardAdapter
 */
import Translator from '../components/Translator';

/**
 * Display the first score.
 *
 * @param {String} text Text to display.
 * @param {Object} scoreboard Scoreboard element.
 * @param {Boolean} hideSecond hide type 2 score
 *
 * @returns {void}
 *
 * @private
 */
var displayFirstScore = function (text, scoreboard, hideSecond) {

    scoreboard.find('.type-1-score')
        .show();
    scoreboard.find('.type-1-score > div')
        .text(text);

    if (hideSecond !== 'undefined' && hideSecond === true) {
        scoreboard.find('.type-2-score').hide();
    }
};

/**
 * Display the second score.
 *
 * @param {String} text Text to display.
 * @param {Object} scoreboard Scoreboard element.
 * @param {Boolean} hideFirst hide type 1 score
 *
 * @returns {void}
 *
 * @private
 */
var displaySecondScore = function (text, scoreboard, hideFirst) {

    scoreboard.find('.type-2-score').show();
    scoreboard.find('.type-2-score > div').text(text);

    if (hideFirst !== 'undefined' && hideFirst === true) {
        scoreboard.find('.type-1-score').hide();
    }
};

/**
 * method to determine whether or not we should be displaying a text type
 *
 * @param scores
 *
 * @returns {boolean}
 *
 * @private
 */
var hasDisplayCommentType = function (scores) {

    return ((scores.leg === '') || (scores.set === ''));
};

/**
 * method to check if this is a score type that we display
 *
 * @param scoreType
 *
 * @returns {boolean}
 *
 * @private
 */
var isValidScoreType = function (scoreType) {

    return scoreType !== 'GEN' && scoreType !== 'Live';
};

/**
 * method to render the comment type
 *
 * @param {object} scoreboard
 * @param {object} scores
 * @param {String} scoreType
 *
 * @returns {void}
 *
 * @private
 */
var renderCommentType = function (scoreboard, scores, scoreType) {

    if (isValidScoreType(scoreType)) {
        renderComment(scoreboard, scores, scoreType);
    }
};

/**
 * render comment type when there is no score
 *
 * @param {Object} scoreboard
 * @param {Object} scores
 * @param {String} scoreType
 *
 * @returns {void}
 *
 * @private
 */
var noScoreRender = function (scoreboard, scores, scoreType) {

    displaySecondScore(scoreType, scoreboard, true);
};

/**
 * render comment type when there is a score
 *
 * @param {Object} scoreboard
 * @param {Object} scores
 * @param {String} scoreType
 *
 * @returns {void}
 *
 * @private
 */
var scoreRender = function (scoreboard, scores, scoreType) {

    if (scores.leg === '') {
        displaySecondScore(scoreType, scoreboard, false);
    } else {
        displayFirstScore(scoreType, scoreboard, false);
    }
};

/**
 * method to render the comment based on if leg or set is unpopulated or both
 *
 * @param {object} scoreboard
 * @param {object} scores
 * @param {String} scoreType
 *
 * @returns {void}
 *
 * @private
 */
var renderComment = function (scoreboard, scores, scoreType) {

    if (scores.leg === '' && scores.set === '') {
        noScoreRender(scoreboard, scores, scoreType);
    } else {
        scoreRender(scoreboard, scores, scoreType);
    }
};

var api = {

    /**
     * Updates score on scoreboard
     *
     * @param  {Object} scores Object that contains set scote, type, leg score & live commentary
     *
     * @returns {Object}
     *
     * @public
     */
    api: function (container) {

        /**
         * Update data
         *
         * @param  {Object} scores Object that contains set score, type, leg score & live commentary
         *
         * @returns {void}
         *
         * @private
         */
        var update = function (scores) {

            var scoreboard = container.find('article#livescore-banner');

            /**
             * Since we have a few conditions to meet for displaying
             * live scores, this is the function that enables all the
             * display logic.
             *
             * @param {Object} livescore jQuery Element that represents the livescore
             *
             * @returns {void}
             *
             * @private
             */
            var setData = function (scoreboard) {

                /**
                 * Logic to display live comments
                 *
                 * @param scores
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                var displayComments = function (scores) {

                    if (scores.commentary) {
                        scoreboard.find('.live-commentary')
                            .show();
                        scoreboard.find('.live-commentary p')
                            .text(scores.commentary);
                    } else {
                        scoreboard.find('.live-commentary')
                            .hide();
                        scoreboard.find('.live-commentary p')
                            .text('');
                    }
                };

                /**
                 * Logic to display comment type or score in the first score.
                 * Display the text in any comment type but empty or GEN
                 *
                 * @param scores
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                var displayCommentType = function (scores) {

                    /**
                     * Returns text to display based on comment type
                     * 'LIVE' for the default one
                     *
                     * @param type
                     *
                     * @returns {*}
                     *
                     * @private
                     */
                    var scoreTypeToText = function (type) {

                        var key = 'components.scoreboards.type.' + type;

                        if (Translator.hasTranslation(key)) {

                            return Translator.translate(key);
                        }

                        return Translator.translate('components.scoreboards.type.LIVE');
                    };

                    if (hasDisplayCommentType(scores)) {
                        renderCommentType(
                            scoreboard,
                            scores,
                            scoreTypeToText(scores.type)
                        );
                    }

                    displayComments(scores);
                };

                /**
                 * Renders scores
                 *
                 * @returns {void}
                 *
                 * @private
                 */
                var renderScores = function () {

                    var shouldShowSetAndScores = function () {

                        return scores.set && scores.leg === '';
                    };

                    var shouldShowSetAndLeg = function () {

                        return scores.set && scores.leg !== '';
                    };

                    var shouldShowLegAndScores = function () {

                        return !scores.set && scores.leg;
                    };

                    var shouldShowDefault = function () {

                        return !scores.set && !scores.leg && typeof scores.type === 'undefined';
                    };

                    var processScores = function () {

                        var processSetAndScores = function () {

                            if (shouldShowSetAndScores()) {

                                displayFirstScore(scores.set, scoreboard, true);
                            }
                        };
                        var processSetAndLeg = function () {

                            if (shouldShowSetAndLeg()) {

                                displayFirstScore(scores.set, scoreboard, false);
                                displaySecondScore(scores.leg, scoreboard, false);
                            }
                        };

                        var processLegAndScores = function () {

                            if (shouldShowLegAndScores()) {

                                displaySecondScore(scores.leg, scoreboard, true);
                            }
                        };

                        var processConditions = function () {

                            processSetAndScores();
                            processSetAndLeg();
                            processLegAndScores();
                            displayComments(scores);
                            displayCommentType(scores);
                        };

                        (function () {

                            if (shouldShowDefault()) {
                                scoreboard.find('.live-commentary')
                                    .addClass('displayNone');
                                scoreboard.find('.score-box')
                                    .addClass('displayNone');
                            } else {
                                scoreboard.find('.live-commentary')
                                    .removeClass('displayNone');
                                scoreboard.find('.score-box')
                                    .removeClass('displayNone');
                                processConditions();
                            }
                        })();
                    };

                    processScores();
                };

                renderScores();
                scoreboard.children('div')
                    .show();
            };

            setData(scoreboard);
            scoreboard.show();
        };

        return {
            update: update,
        };
    },
};

export default api;
